import React, {useCallback, useEffect, useMemo} from "react";
import {observer} from "mobx-react";
import {LayoutPagePromptManagement} from "../components";
import {usePromptManagementStore} from "../hooks";
import dayjs from "dayjs";
import {CategoryBadge} from "../components/CategoryBadge";
import {MaterialReactTable, useMaterialReactTable} from "material-react-table";
import {
  paginationProps,
  tableStyles,
} from "./PromptManagementList.styled";
import {getDensity} from "../../../../utils/GetDensity/GetDensity";
import {useMediaQueries} from "../../../../utils/mediaQueries";
import {useNavigate} from "react-router-dom";
import {PromptManagementSystemPagesTypes} from "../types";
import {useStore} from "../../../../hooks";

export const PromptManagementList = observer(() => {
  const { authStore } = useStore();
  const store = usePromptManagementStore();
  const { listPrompt, isLoading } = store;
  const navigate = useNavigate();
  const { isMobile, isTablet, isDesktop } = useMediaQueries();
  const sorting = [{ id: "name", desc: false }];

  useEffect(() => {
    store.onChangePage(authStore.selectedAccount.id, PromptManagementSystemPagesTypes.PAGE_LIST);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [store]);
  
  const onHandleSelectRow = useCallback((id) => {
    navigate(`/v2/prompt-management-system/${id}`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  
  const density = useMemo(
    () => getDensity(isMobile, isTablet, isDesktop),
    [isMobile, isTablet, isDesktop]
  );
  
  const columns = useMemo(() => [
    {
      accessorKey: "name",
      header: "Name",
      size: 150,
      enableSorting: true,
    },
    {
      accessorKey: "shortDescription",
      header: "Description",
      size: 250,
      enableSorting: true,
    },
    {
      accessorKey: "category",
      header: "Category",
      Cell: ({ cell }) => (
        <CategoryBadge category={cell.getValue()?.toLowerCase()} />
      ),
      size: 150,
      enableSorting: true,
    },
    {
      accessorKey: "updatedAt",
      header: "Updated",
      filterVariant: "date",
      sortingFn: "datetime",
      Cell: ({ cell }) =>
        cell.getValue()
          ? dayjs(cell.getValue()).format("MM/DD/YYYY - HH:mm")
          : "---",
      size: 200,
      enableSorting: true,
    },
  ], []);

  const table = useMaterialReactTable({
    columns,
    data: listPrompt,
    enableRowSelection: true,
    enableMultiRowSelection: true,
    enableColumnFilters: true,
    enableGlobalFilter: true,
    enableColumnResizing: true,
    enableSorting: true,
    enablePagination: true,
    enableTopToolbar: true,
    muiSearchTextFieldProps: {
      variant: 'outlined',
      placeholder: 'Search prompts ...',
      sx: tableStyles.searchField.sx,
    },
    initialState: {
      sorting,
      density: density,
      pagination: {
        pageSize: 10,
        pageIndex: 0,
      },
    },
    state: {
      isLoading,
    },
    muiTableContainerProps: tableStyles.tableContainer,
    muiTablePaperProps: tableStyles.tablePaper,
    muiTopToolbarProps: tableStyles.topToolbar,
    muiTableHeadProps: tableStyles.tableHead,
    muiTableHeadCellProps: tableStyles.tableHeadCell,
    muiBottomToolbarProps: tableStyles.bottomToolbar,
    muiPaginationProps: paginationProps,
    muiTableBodyRowProps: ({ row }) => ({
      onClick: () => onHandleSelectRow(row.original.id)
    })
  });
  
  return (
    <LayoutPagePromptManagement>
      <MaterialReactTable table={table} />
    </LayoutPagePromptManagement>
  );
});
